/**
 * Vague 3 « expérience école » : écran Séances (`pages/institution/InstitutionTrainingSessionsPanel.tsx`),
 * notes sur 20 (`components/institution/EvaluationManagement.tsx`, `components/learner/LearnerEvaluationRecap.tsx`),
 * « Mes classes » du profil (`pages/profile/ProfilePage.tsx`) et vocabulaire de l’espace élève (`pages/learner/LearnerPage.tsx`).
 *
 * Même mécanisme que `i18n-education-segment.ts` / `i18n-classes-evaluations-locales.ts` : clés plates,
 * variante scolaire suffixée `_education` (lue via `useSegmentTranslation()` ou `t(key, { context })`).
 * fr / en sont chargés au démarrage ; les 9 autres langues sont fusionnées dans leur bundle chargé à la demande
 * (`lazySlices` de `lib/i18n.ts`). Toutes les clés existent dans les 11 langues.
 * Vérification : `npx vite-node scripts/check-i18n-school-experience.ts`.
 */
import type { UiLanguageCode } from '@/lib/ui-languages';

export const schoolExperienceLocales: Record<UiLanguageCode, Record<string, string>> = {
  fr: {
    "institution.tabs.trainingSessions": "Sessions",
    "institution.tabs.trainingSessions_education": "Séances",
    "institution.trainingSessions.title": "Sessions de formation",
    "institution.trainingSessions.title_education": "Séances de cours",
    "institution.trainingSessions.subtitle": "Planifiez les sessions, inscrivez les apprenants, suivez les présences et la traçabilité (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Planifiez les séances de cours de vos classes et faites l’appel.",
    "institution.trainingSessions.ownScopeHint": "Vous voyez les sessions que vous animez ou que vous avez créées.",
    "institution.trainingSessions.ownScopeHint_education": "Vous voyez les séances dont vous êtes l’enseignant ou l’auteur.",
    "institution.trainingSessions.refresh": "Actualiser",
    "institution.trainingSessions.new": "Nouvelle session",
    "institution.trainingSessions.new_education": "Nouvelle séance",
    "institution.trainingSessions.loadError": "Impossible de charger les sessions.",
    "institution.trainingSessions.loadError_education": "Impossible de charger les séances.",
    "institution.trainingSessions.search": "Rechercher (intitulé, code)…",
    "institution.trainingSessions.statusAll": "Tous les statuts",
    "institution.trainingSessions.status.draft": "Brouillon",
    "institution.trainingSessions.status.planned": "Planifiée",
    "institution.trainingSessions.status.in_progress": "En cours",
    "institution.trainingSessions.status.completed": "Terminée",
    "institution.trainingSessions.status.cancelled": "Annulée",
    "institution.trainingSessions.empty": "Aucune session.",
    "institution.trainingSessions.empty_education": "Aucune séance.",
    "institution.trainingSessions.total": "Total : {{count}}",
    "institution.trainingSessions.detailHint": "Sélectionnez une session dans la liste ou créez-en une.",
    "institution.trainingSessions.detailHint_education": "Sélectionnez une séance dans la liste ou créez-en une.",
    "institution.trainingSessions.formFlow": "Formation",
    "institution.trainingSessions.formFlow_education": "Cours",
    "institution.trainingSessions.formCode": "Code de la session",
    "institution.trainingSessions.formCode_education": "Code de la séance",
    "institution.trainingSessions.suggestCode": "Suggérer",
    "institution.trainingSessions.formTitle": "Intitulé",
    "institution.trainingSessions.formModality": "Modalité",
    "institution.trainingSessions.modality.presentiel": "Présentiel",
    "institution.trainingSessions.modality.presentiel_education": "En classe",
    "institution.trainingSessions.modality.distanciel": "Distanciel",
    "institution.trainingSessions.modality.distanciel_education": "À distance",
    "institution.trainingSessions.modality.mixte": "Mixte",
    "institution.trainingSessions.modality.mixte_education": "Hybride",
    "institution.trainingSessions.formStatus": "Statut",
    "institution.trainingSessions.formStart": "Début",
    "institution.trainingSessions.formEnd": "Fin",
    "institution.trainingSessions.formLocation": "Lieu",
    "institution.trainingSessions.formLocation_education": "Salle",
    "institution.trainingSessions.formVisio": "Lien de visioconférence",
    "institution.trainingSessions.formMaxSeats": "Places maximum",
    "institution.trainingSessions.formTrainer": "Formateur / animateur",
    "institution.trainingSessions.formTrainer_education": "Enseignant",
    "institution.trainingSessions.formTrainerNone": "— Aucun —",
    "institution.trainingSessions.formGroup": "Groupe d’apprenants",
    "institution.trainingSessions.formGroup_education": "Groupe d’élèves",
    "institution.trainingSessions.formGroupNone": "— Aucun —",
    "institution.trainingSessions.sectionQualiopi": "Traçabilité Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "Convocation envoyée le",
    "institution.trainingSessions.programmeSent": "Programme envoyé le",
    "institution.trainingSessions.rulesSent": "Règlement intérieur envoyé le",
    "institution.trainingSessions.dropoutNotes": "Suivi des abandons (actions menées)",
    "institution.trainingSessions.adminNotes": "Notes administratives (financement, BPF)",
    "institution.trainingSessions.save": "Enregistrer",
    "institution.trainingSessions.saveError": "Enregistrement impossible.",
    "institution.trainingSessions.requiredFields": "Renseignez la formation, l’intitulé, le début et la fin.",
    "institution.trainingSessions.requiredFields_education": "Renseignez le cours, l’intitulé, le début et la fin.",
    "institution.trainingSessions.actionError": "Action impossible.",
    "institution.trainingSessions.delete": "Supprimer",
    "institution.trainingSessions.deleteConfirm": "Supprimer cette session ?",
    "institution.trainingSessions.deleteConfirm_education": "Supprimer cette séance ?",
    "institution.trainingSessions.attendees": "Inscrits",
    "institution.trainingSessions.attendees_education": "Élèves",
    "institution.trainingSessions.addAttendees": "Inscrire des apprenants",
    "institution.trainingSessions.addAttendees_education": "Ajouter des élèves",
    "institution.trainingSessions.learnerSearch": "Rechercher un apprenant (2 caractères minimum)",
    "institution.trainingSessions.learnerSearch_education": "Rechercher un élève (2 caractères minimum)",
    "institution.trainingSessions.enrollInFlow": "Inscrire aussi à la formation",
    "institution.trainingSessions.enrollInFlow_education": "Donner aussi accès au cours",
    "institution.trainingSessions.addSelected": "Ajouter la sélection",
    "institution.trainingSessions.removeAttendee": "Retirer",
    "institution.trainingSessions.attendeeConvocation": "Convocation envoyée le",
    "institution.trainingSessions.attendeeDropout": "Actions contre l’abandon",
    "institution.trainingSessions.attended": "Présence confirmée",
    "institution.trainingSessions.attended_education": "Présent",
    "institution.trainingSessions.withdrawn": "Abandon le",
    "institution.trainingSessions.saveAttendee": "Enregistrer",
    "institution.trainingSessions.edit": "Modifier",
    "institution.trainingSessions.cancel": "Annuler",
    "institution.trainingSessions.classes": "Classes",
    "institution.trainingSessions.classFilterAll": "Toutes les classes",
    "institution.trainingSessions.addClass": "Ajouter toute une classe",
    "institution.trainingSessions.chooseClass": "Choisir une classe",
    "institution.trainingSessions.addClassButton": "Ajouter la classe",
    "institution.trainingSessions.rollCall": "Appel",
    "institution.trainingSessions.present": "Présent",
    "institution.trainingSessions.absent": "Absent",
    "institution.trainingSessions.presentCount": "Présents : {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "Tous présents",
    "institution.evaluations.classAverage": "Moyenne de la classe",
    "institution.evaluations.classAverage_education": "Moyenne de classe",
    "institution.evaluations.gradedCount": "Notés : {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Élèves notés : {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Pas encore de note pour calculer une moyenne.",
    "institution.evaluations.gradeOn20": "Note /20",
    "institution.evaluations.rawPoints": "{{value}} pts",
    "profile.school.title": "Mes classes",
    "profile.school.titleTeacher": "Classes que j’anime",
    "profile.school.empty": "Aucune classe pour le moment.",
    "profile.school.loadError": "Impossible de charger vos classes.",
    "profile.school.teachers": "Enseignants",
    "profile.school.noTeacher": "Aucun enseignant indiqué.",
    "profile.school.leadSuffix": "professeur principal",
    "profile.school.role": "Profil",
    "profile.school.classRole.student": "Élève",
    "profile.school.classRole.teacher": "Enseignant",
    "profile.school.classRole.lead": "Professeur principal",
    "profile.school.profileRole.student": "Élève",
    "profile.school.profileRole.teacher": "Enseignant",
    "profile.school.profileRole.direction": "Direction",
    "learner.header.workspace_education": "Espace élève",
    "learner.tabs.formations_education": "Mes cours",
    "learner.sidebar.tagline_education": "Élève",
    "learner.formations.title_education": "Mes cours",
    "learner.formations.searchPlaceholder_education": "Rechercher un cours…",
    "learner.formations.loading_education": "Chargement de vos cours…",
    "learner.formations.empty_education": "Aucun cours trouvé.",
    "learner.formations.instructor_education": "Enseignant",
    "learner.home.defaultName_education": "élève",
    "learner.home.resumeMyFormation_education": "Reprendre mon cours",
    "learner.home.resumeTraining_education": "Reprendre vos cours",
    "learner.progress.completedFormations_education": "Cours terminés",
    "learner.sidebar.backToTrainer_education": "Tableau de bord enseignant",
    "learner.sidebar.backToTrainerAria_education": "Retour au tableau de bord enseignant",
  },
  en: {
    "institution.tabs.trainingSessions": "Sessions",
    "institution.tabs.trainingSessions_education": "Lessons",
    "institution.trainingSessions.title": "Training sessions",
    "institution.trainingSessions.title_education": "Class sessions",
    "institution.trainingSessions.subtitle": "Schedule sessions, enrol learners, track attendance and compliance records (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Schedule your classes’ lessons and take the register.",
    "institution.trainingSessions.ownScopeHint": "You see the sessions you run or created.",
    "institution.trainingSessions.ownScopeHint_education": "You see the lessons you teach or created.",
    "institution.trainingSessions.refresh": "Refresh",
    "institution.trainingSessions.new": "New session",
    "institution.trainingSessions.new_education": "New lesson",
    "institution.trainingSessions.loadError": "Unable to load sessions.",
    "institution.trainingSessions.loadError_education": "Unable to load lessons.",
    "institution.trainingSessions.search": "Search (title, code)…",
    "institution.trainingSessions.statusAll": "All statuses",
    "institution.trainingSessions.status.draft": "Draft",
    "institution.trainingSessions.status.planned": "Planned",
    "institution.trainingSessions.status.in_progress": "In progress",
    "institution.trainingSessions.status.completed": "Completed",
    "institution.trainingSessions.status.cancelled": "Cancelled",
    "institution.trainingSessions.empty": "No sessions.",
    "institution.trainingSessions.empty_education": "No lessons.",
    "institution.trainingSessions.total": "Total: {{count}}",
    "institution.trainingSessions.detailHint": "Select a session in the list or create one.",
    "institution.trainingSessions.detailHint_education": "Select a lesson in the list or create one.",
    "institution.trainingSessions.formFlow": "Course",
    "institution.trainingSessions.formFlow_education": "Course",
    "institution.trainingSessions.formCode": "Session code",
    "institution.trainingSessions.formCode_education": "Lesson code",
    "institution.trainingSessions.suggestCode": "Suggest",
    "institution.trainingSessions.formTitle": "Title",
    "institution.trainingSessions.formModality": "Format",
    "institution.trainingSessions.modality.presentiel": "In person",
    "institution.trainingSessions.modality.presentiel_education": "In class",
    "institution.trainingSessions.modality.distanciel": "Remote",
    "institution.trainingSessions.modality.distanciel_education": "Remote",
    "institution.trainingSessions.modality.mixte": "Blended",
    "institution.trainingSessions.modality.mixte_education": "Hybrid",
    "institution.trainingSessions.formStatus": "Status",
    "institution.trainingSessions.formStart": "Start",
    "institution.trainingSessions.formEnd": "End",
    "institution.trainingSessions.formLocation": "Location",
    "institution.trainingSessions.formLocation_education": "Room",
    "institution.trainingSessions.formVisio": "Video conference link",
    "institution.trainingSessions.formMaxSeats": "Maximum seats",
    "institution.trainingSessions.formTrainer": "Trainer / facilitator",
    "institution.trainingSessions.formTrainer_education": "Teacher",
    "institution.trainingSessions.formTrainerNone": "— None —",
    "institution.trainingSessions.formGroup": "Learner group",
    "institution.trainingSessions.formGroup_education": "Student group",
    "institution.trainingSessions.formGroupNone": "— None —",
    "institution.trainingSessions.sectionQualiopi": "Qualiopi / BPF records",
    "institution.trainingSessions.convocationSent": "Notice sent on",
    "institution.trainingSessions.programmeSent": "Programme sent on",
    "institution.trainingSessions.rulesSent": "Internal rules sent on",
    "institution.trainingSessions.dropoutNotes": "Drop-out follow-up (actions taken)",
    "institution.trainingSessions.adminNotes": "Administrative notes (funding, BPF)",
    "institution.trainingSessions.save": "Save",
    "institution.trainingSessions.saveError": "Unable to save.",
    "institution.trainingSessions.requiredFields": "Fill in the course, title, start and end.",
    "institution.trainingSessions.requiredFields_education": "Fill in the course, title, start and end.",
    "institution.trainingSessions.actionError": "Action failed.",
    "institution.trainingSessions.delete": "Delete",
    "institution.trainingSessions.deleteConfirm": "Delete this session?",
    "institution.trainingSessions.deleteConfirm_education": "Delete this lesson?",
    "institution.trainingSessions.attendees": "Enrolled",
    "institution.trainingSessions.attendees_education": "Students",
    "institution.trainingSessions.addAttendees": "Enrol learners",
    "institution.trainingSessions.addAttendees_education": "Add students",
    "institution.trainingSessions.learnerSearch": "Search for a learner (at least 2 characters)",
    "institution.trainingSessions.learnerSearch_education": "Search for a student (at least 2 characters)",
    "institution.trainingSessions.enrollInFlow": "Also enrol in the course",
    "institution.trainingSessions.enrollInFlow_education": "Also give access to the course",
    "institution.trainingSessions.addSelected": "Add selection",
    "institution.trainingSessions.removeAttendee": "Remove",
    "institution.trainingSessions.attendeeConvocation": "Notice sent on",
    "institution.trainingSessions.attendeeDropout": "Drop-out prevention actions",
    "institution.trainingSessions.attended": "Attendance confirmed",
    "institution.trainingSessions.attended_education": "Present",
    "institution.trainingSessions.withdrawn": "Dropped out on",
    "institution.trainingSessions.saveAttendee": "Save",
    "institution.trainingSessions.edit": "Edit",
    "institution.trainingSessions.cancel": "Cancel",
    "institution.trainingSessions.classes": "Classes",
    "institution.trainingSessions.classFilterAll": "All classes",
    "institution.trainingSessions.addClass": "Add a whole class",
    "institution.trainingSessions.chooseClass": "Choose a class",
    "institution.trainingSessions.addClassButton": "Add the class",
    "institution.trainingSessions.rollCall": "Register",
    "institution.trainingSessions.present": "Present",
    "institution.trainingSessions.absent": "Absent",
    "institution.trainingSessions.presentCount": "Present: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "All present",
    "institution.evaluations.classAverage": "Class average",
    "institution.evaluations.classAverage_education": "Class average",
    "institution.evaluations.gradedCount": "Graded: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Students graded: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "No grades yet to compute an average.",
    "institution.evaluations.gradeOn20": "Grade /20",
    "institution.evaluations.rawPoints": "{{value}} pts",
    "profile.school.title": "My classes",
    "profile.school.titleTeacher": "Classes I teach",
    "profile.school.empty": "No classes yet.",
    "profile.school.loadError": "Unable to load your classes.",
    "profile.school.teachers": "Teachers",
    "profile.school.noTeacher": "No teacher listed.",
    "profile.school.leadSuffix": "form tutor",
    "profile.school.role": "Profile",
    "profile.school.classRole.student": "Student",
    "profile.school.classRole.teacher": "Teacher",
    "profile.school.classRole.lead": "Form tutor",
    "profile.school.profileRole.student": "Student",
    "profile.school.profileRole.teacher": "Teacher",
    "profile.school.profileRole.direction": "School management",
    "learner.header.workspace_education": "Student space",
    "learner.tabs.formations_education": "My courses",
    "learner.sidebar.tagline_education": "Student",
    "learner.formations.title_education": "My courses",
    "learner.formations.searchPlaceholder_education": "Search for a course…",
    "learner.formations.loading_education": "Loading your courses…",
    "learner.formations.empty_education": "No course found.",
    "learner.formations.instructor_education": "Teacher",
    "learner.home.defaultName_education": "student",
    "learner.home.resumeMyFormation_education": "Resume my course",
    "learner.home.resumeTraining_education": "Resume your courses",
    "learner.progress.completedFormations_education": "Courses completed",
    "learner.sidebar.backToTrainer_education": "Teacher dashboard",
    "learner.sidebar.backToTrainerAria_education": "Back to the teacher dashboard",
  },
  de: {
    "institution.tabs.trainingSessions": "Sitzungen",
    "institution.tabs.trainingSessions_education": "Unterrichtsstunden",
    "institution.trainingSessions.title": "Schulungssitzungen",
    "institution.trainingSessions.title_education": "Unterrichtsstunden",
    "institution.trainingSessions.subtitle": "Sitzungen planen, Lernende anmelden, Anwesenheit und Nachweise verfolgen (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Planen Sie die Unterrichtsstunden Ihrer Klassen und nehmen Sie die Anwesenheit auf.",
    "institution.trainingSessions.ownScopeHint": "Sie sehen die Sitzungen, die Sie leiten oder erstellt haben.",
    "institution.trainingSessions.ownScopeHint_education": "Sie sehen die Stunden, die Sie unterrichten oder erstellt haben.",
    "institution.trainingSessions.refresh": "Aktualisieren",
    "institution.trainingSessions.new": "Neue Sitzung",
    "institution.trainingSessions.new_education": "Neue Stunde",
    "institution.trainingSessions.loadError": "Sitzungen können nicht geladen werden.",
    "institution.trainingSessions.loadError_education": "Stunden können nicht geladen werden.",
    "institution.trainingSessions.search": "Suchen (Titel, Code)…",
    "institution.trainingSessions.statusAll": "Alle Status",
    "institution.trainingSessions.status.draft": "Entwurf",
    "institution.trainingSessions.status.planned": "Geplant",
    "institution.trainingSessions.status.in_progress": "Läuft",
    "institution.trainingSessions.status.completed": "Abgeschlossen",
    "institution.trainingSessions.status.cancelled": "Abgesagt",
    "institution.trainingSessions.empty": "Keine Sitzungen.",
    "institution.trainingSessions.empty_education": "Keine Stunden.",
    "institution.trainingSessions.total": "Gesamt: {{count}}",
    "institution.trainingSessions.detailHint": "Wählen Sie eine Sitzung in der Liste aus oder erstellen Sie eine.",
    "institution.trainingSessions.detailHint_education": "Wählen Sie eine Stunde in der Liste aus oder erstellen Sie eine.",
    "institution.trainingSessions.formFlow": "Schulung",
    "institution.trainingSessions.formFlow_education": "Kurs",
    "institution.trainingSessions.formCode": "Sitzungscode",
    "institution.trainingSessions.formCode_education": "Stundencode",
    "institution.trainingSessions.suggestCode": "Vorschlagen",
    "institution.trainingSessions.formTitle": "Titel",
    "institution.trainingSessions.formModality": "Format",
    "institution.trainingSessions.modality.presentiel": "Präsenz",
    "institution.trainingSessions.modality.presentiel_education": "Im Klassenzimmer",
    "institution.trainingSessions.modality.distanciel": "Fernunterricht",
    "institution.trainingSessions.modality.distanciel_education": "Aus der Ferne",
    "institution.trainingSessions.modality.mixte": "Gemischt",
    "institution.trainingSessions.modality.mixte_education": "Hybrid",
    "institution.trainingSessions.formStatus": "Status",
    "institution.trainingSessions.formStart": "Beginn",
    "institution.trainingSessions.formEnd": "Ende",
    "institution.trainingSessions.formLocation": "Ort",
    "institution.trainingSessions.formLocation_education": "Raum",
    "institution.trainingSessions.formVisio": "Link zur Videokonferenz",
    "institution.trainingSessions.formMaxSeats": "Maximale Plätze",
    "institution.trainingSessions.formTrainer": "Trainer / Moderator",
    "institution.trainingSessions.formTrainer_education": "Lehrkraft",
    "institution.trainingSessions.formTrainerNone": "— Keiner —",
    "institution.trainingSessions.formGroup": "Lerngruppe",
    "institution.trainingSessions.formGroup_education": "Schülergruppe",
    "institution.trainingSessions.formGroupNone": "— Keine —",
    "institution.trainingSessions.sectionQualiopi": "Nachweise Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "Einladung gesendet am",
    "institution.trainingSessions.programmeSent": "Programm gesendet am",
    "institution.trainingSessions.rulesSent": "Hausordnung gesendet am",
    "institution.trainingSessions.dropoutNotes": "Abbruch-Nachverfolgung (Maßnahmen)",
    "institution.trainingSessions.adminNotes": "Verwaltungsnotizen (Finanzierung, BPF)",
    "institution.trainingSessions.save": "Speichern",
    "institution.trainingSessions.saveError": "Speichern nicht möglich.",
    "institution.trainingSessions.requiredFields": "Geben Sie Schulung, Titel, Beginn und Ende an.",
    "institution.trainingSessions.requiredFields_education": "Geben Sie Kurs, Titel, Beginn und Ende an.",
    "institution.trainingSessions.actionError": "Aktion nicht möglich.",
    "institution.trainingSessions.delete": "Löschen",
    "institution.trainingSessions.deleteConfirm": "Diese Sitzung löschen?",
    "institution.trainingSessions.deleteConfirm_education": "Diese Stunde löschen?",
    "institution.trainingSessions.attendees": "Angemeldete",
    "institution.trainingSessions.attendees_education": "Schüler",
    "institution.trainingSessions.addAttendees": "Lernende anmelden",
    "institution.trainingSessions.addAttendees_education": "Schüler hinzufügen",
    "institution.trainingSessions.learnerSearch": "Lernende suchen (mindestens 2 Zeichen)",
    "institution.trainingSessions.learnerSearch_education": "Schüler suchen (mindestens 2 Zeichen)",
    "institution.trainingSessions.enrollInFlow": "Auch in der Schulung anmelden",
    "institution.trainingSessions.enrollInFlow_education": "Auch Zugang zum Kurs geben",
    "institution.trainingSessions.addSelected": "Auswahl hinzufügen",
    "institution.trainingSessions.removeAttendee": "Entfernen",
    "institution.trainingSessions.attendeeConvocation": "Einladung gesendet am",
    "institution.trainingSessions.attendeeDropout": "Maßnahmen gegen Abbruch",
    "institution.trainingSessions.attended": "Anwesenheit bestätigt",
    "institution.trainingSessions.attended_education": "Anwesend",
    "institution.trainingSessions.withdrawn": "Abgebrochen am",
    "institution.trainingSessions.saveAttendee": "Speichern",
    "institution.trainingSessions.edit": "Bearbeiten",
    "institution.trainingSessions.cancel": "Abbrechen",
    "institution.trainingSessions.classes": "Klassen",
    "institution.trainingSessions.classFilterAll": "Alle Klassen",
    "institution.trainingSessions.addClass": "Eine ganze Klasse hinzufügen",
    "institution.trainingSessions.chooseClass": "Klasse auswählen",
    "institution.trainingSessions.addClassButton": "Klasse hinzufügen",
    "institution.trainingSessions.rollCall": "Anwesenheit",
    "institution.trainingSessions.present": "Anwesend",
    "institution.trainingSessions.absent": "Abwesend",
    "institution.trainingSessions.presentCount": "Anwesend: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "Alle anwesend",
    "institution.evaluations.classAverage": "Klassendurchschnitt",
    "institution.evaluations.classAverage_education": "Klassendurchschnitt",
    "institution.evaluations.gradedCount": "Bewertet: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Bewertete Schüler: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Noch keine Noten für einen Durchschnitt.",
    "institution.evaluations.gradeOn20": "Note /20",
    "institution.evaluations.rawPoints": "{{value}} Pkt.",
    "profile.school.title": "Meine Klassen",
    "profile.school.titleTeacher": "Klassen, die ich unterrichte",
    "profile.school.empty": "Noch keine Klasse.",
    "profile.school.loadError": "Ihre Klassen können nicht geladen werden.",
    "profile.school.teachers": "Lehrkräfte",
    "profile.school.noTeacher": "Keine Lehrkraft angegeben.",
    "profile.school.leadSuffix": "Klassenlehrkraft",
    "profile.school.role": "Profil",
    "profile.school.classRole.student": "Schüler",
    "profile.school.classRole.teacher": "Lehrkraft",
    "profile.school.classRole.lead": "Klassenlehrkraft",
    "profile.school.profileRole.student": "Schüler",
    "profile.school.profileRole.teacher": "Lehrkraft",
    "profile.school.profileRole.direction": "Schulleitung",
    "learner.header.workspace_education": "Schülerbereich",
    "learner.tabs.formations_education": "Meine Kurse",
    "learner.sidebar.tagline_education": "Schüler",
    "learner.formations.title_education": "Meine Kurse",
    "learner.formations.searchPlaceholder_education": "Kurs suchen…",
    "learner.formations.loading_education": "Ihre Kurse werden geladen…",
    "learner.formations.empty_education": "Kein Kurs gefunden.",
    "learner.formations.instructor_education": "Lehrkraft",
    "learner.home.defaultName_education": "Schüler",
    "learner.home.resumeMyFormation_education": "Meinen Kurs fortsetzen",
    "learner.home.resumeTraining_education": "Kurse fortsetzen",
    "learner.progress.completedFormations_education": "Abgeschlossene Kurse",
    "learner.sidebar.backToTrainer_education": "Lehrkraft-Dashboard",
    "learner.sidebar.backToTrainerAria_education": "Zurück zum Lehrkraft-Dashboard",
  },
  es: {
    "institution.tabs.trainingSessions": "Sesiones",
    "institution.tabs.trainingSessions_education": "Clases",
    "institution.trainingSessions.title": "Sesiones de formación",
    "institution.trainingSessions.title_education": "Sesiones de clase",
    "institution.trainingSessions.subtitle": "Planifique las sesiones, inscriba a los alumnos y siga la asistencia y la trazabilidad (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Planifique las sesiones de clase de sus grupos y pase lista.",
    "institution.trainingSessions.ownScopeHint": "Ve las sesiones que imparte o que ha creado.",
    "institution.trainingSessions.ownScopeHint_education": "Ve las sesiones de las que es docente o autor.",
    "institution.trainingSessions.refresh": "Actualizar",
    "institution.trainingSessions.new": "Nueva sesión",
    "institution.trainingSessions.new_education": "Nueva sesión de clase",
    "institution.trainingSessions.loadError": "No se pueden cargar las sesiones.",
    "institution.trainingSessions.loadError_education": "No se pueden cargar las sesiones de clase.",
    "institution.trainingSessions.search": "Buscar (título, código)…",
    "institution.trainingSessions.statusAll": "Todos los estados",
    "institution.trainingSessions.status.draft": "Borrador",
    "institution.trainingSessions.status.planned": "Planificada",
    "institution.trainingSessions.status.in_progress": "En curso",
    "institution.trainingSessions.status.completed": "Terminada",
    "institution.trainingSessions.status.cancelled": "Cancelada",
    "institution.trainingSessions.empty": "Ninguna sesión.",
    "institution.trainingSessions.empty_education": "Ninguna sesión de clase.",
    "institution.trainingSessions.total": "Total: {{count}}",
    "institution.trainingSessions.detailHint": "Seleccione una sesión de la lista o cree una.",
    "institution.trainingSessions.detailHint_education": "Seleccione una sesión de clase de la lista o cree una.",
    "institution.trainingSessions.formFlow": "Formación",
    "institution.trainingSessions.formFlow_education": "Curso",
    "institution.trainingSessions.formCode": "Código de la sesión",
    "institution.trainingSessions.formCode_education": "Código de la sesión de clase",
    "institution.trainingSessions.suggestCode": "Sugerir",
    "institution.trainingSessions.formTitle": "Título",
    "institution.trainingSessions.formModality": "Modalidad",
    "institution.trainingSessions.modality.presentiel": "Presencial",
    "institution.trainingSessions.modality.presentiel_education": "En el aula",
    "institution.trainingSessions.modality.distanciel": "A distancia",
    "institution.trainingSessions.modality.distanciel_education": "A distancia",
    "institution.trainingSessions.modality.mixte": "Mixta",
    "institution.trainingSessions.modality.mixte_education": "Híbrida",
    "institution.trainingSessions.formStatus": "Estado",
    "institution.trainingSessions.formStart": "Inicio",
    "institution.trainingSessions.formEnd": "Fin",
    "institution.trainingSessions.formLocation": "Lugar",
    "institution.trainingSessions.formLocation_education": "Aula",
    "institution.trainingSessions.formVisio": "Enlace de videoconferencia",
    "institution.trainingSessions.formMaxSeats": "Plazas máximas",
    "institution.trainingSessions.formTrainer": "Formador / facilitador",
    "institution.trainingSessions.formTrainer_education": "Docente",
    "institution.trainingSessions.formTrainerNone": "— Ninguno —",
    "institution.trainingSessions.formGroup": "Grupo de alumnos",
    "institution.trainingSessions.formGroup_education": "Grupo de alumnos",
    "institution.trainingSessions.formGroupNone": "— Ninguno —",
    "institution.trainingSessions.sectionQualiopi": "Trazabilidad Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "Convocatoria enviada el",
    "institution.trainingSessions.programmeSent": "Programa enviado el",
    "institution.trainingSessions.rulesSent": "Reglamento interno enviado el",
    "institution.trainingSessions.dropoutNotes": "Seguimiento de abandonos (acciones realizadas)",
    "institution.trainingSessions.adminNotes": "Notas administrativas (financiación, BPF)",
    "institution.trainingSessions.save": "Guardar",
    "institution.trainingSessions.saveError": "No se puede guardar.",
    "institution.trainingSessions.requiredFields": "Indique la formación, el título, el inicio y el fin.",
    "institution.trainingSessions.requiredFields_education": "Indique el curso, el título, el inicio y el fin.",
    "institution.trainingSessions.actionError": "Acción imposible.",
    "institution.trainingSessions.delete": "Eliminar",
    "institution.trainingSessions.deleteConfirm": "¿Eliminar esta sesión?",
    "institution.trainingSessions.deleteConfirm_education": "¿Eliminar esta sesión de clase?",
    "institution.trainingSessions.attendees": "Inscritos",
    "institution.trainingSessions.attendees_education": "Alumnos",
    "institution.trainingSessions.addAttendees": "Inscribir alumnos",
    "institution.trainingSessions.addAttendees_education": "Añadir alumnos",
    "institution.trainingSessions.learnerSearch": "Buscar un alumno (mínimo 2 caracteres)",
    "institution.trainingSessions.learnerSearch_education": "Buscar un alumno (mínimo 2 caracteres)",
    "institution.trainingSessions.enrollInFlow": "Inscribir también en la formación",
    "institution.trainingSessions.enrollInFlow_education": "Dar también acceso al curso",
    "institution.trainingSessions.addSelected": "Añadir la selección",
    "institution.trainingSessions.removeAttendee": "Quitar",
    "institution.trainingSessions.attendeeConvocation": "Convocatoria enviada el",
    "institution.trainingSessions.attendeeDropout": "Acciones contra el abandono",
    "institution.trainingSessions.attended": "Asistencia confirmada",
    "institution.trainingSessions.attended_education": "Presente",
    "institution.trainingSessions.withdrawn": "Abandono el",
    "institution.trainingSessions.saveAttendee": "Guardar",
    "institution.trainingSessions.edit": "Editar",
    "institution.trainingSessions.cancel": "Cancelar",
    "institution.trainingSessions.classes": "Clases",
    "institution.trainingSessions.classFilterAll": "Todas las clases",
    "institution.trainingSessions.addClass": "Añadir una clase entera",
    "institution.trainingSessions.chooseClass": "Elegir una clase",
    "institution.trainingSessions.addClassButton": "Añadir la clase",
    "institution.trainingSessions.rollCall": "Pasar lista",
    "institution.trainingSessions.present": "Presente",
    "institution.trainingSessions.absent": "Ausente",
    "institution.trainingSessions.presentCount": "Presentes: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "Todos presentes",
    "institution.evaluations.classAverage": "Media de la clase",
    "institution.evaluations.classAverage_education": "Media de la clase",
    "institution.evaluations.gradedCount": "Calificados: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Alumnos calificados: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Aún no hay notas para calcular una media.",
    "institution.evaluations.gradeOn20": "Nota /20",
    "institution.evaluations.rawPoints": "{{value}} pts",
    "profile.school.title": "Mis clases",
    "profile.school.titleTeacher": "Clases que imparto",
    "profile.school.empty": "Ninguna clase por ahora.",
    "profile.school.loadError": "No se pueden cargar sus clases.",
    "profile.school.teachers": "Docentes",
    "profile.school.noTeacher": "Ningún docente indicado.",
    "profile.school.leadSuffix": "tutor de la clase",
    "profile.school.role": "Perfil",
    "profile.school.classRole.student": "Alumno",
    "profile.school.classRole.teacher": "Docente",
    "profile.school.classRole.lead": "Tutor de la clase",
    "profile.school.profileRole.student": "Alumno",
    "profile.school.profileRole.teacher": "Docente",
    "profile.school.profileRole.direction": "Dirección",
    "learner.header.workspace_education": "Espacio del alumno",
    "learner.tabs.formations_education": "Mis cursos",
    "learner.sidebar.tagline_education": "Alumno",
    "learner.formations.title_education": "Mis cursos",
    "learner.formations.searchPlaceholder_education": "Buscar un curso…",
    "learner.formations.loading_education": "Cargando sus cursos…",
    "learner.formations.empty_education": "Ningún curso encontrado.",
    "learner.formations.instructor_education": "Docente",
    "learner.home.defaultName_education": "alumno",
    "learner.home.resumeMyFormation_education": "Retomar mi curso",
    "learner.home.resumeTraining_education": "Retomar sus cursos",
    "learner.progress.completedFormations_education": "Cursos terminados",
    "learner.sidebar.backToTrainer_education": "Panel del docente",
    "learner.sidebar.backToTrainerAria_education": "Volver al panel del docente",
  },
  it: {
    "institution.tabs.trainingSessions": "Sessioni",
    "institution.tabs.trainingSessions_education": "Lezioni",
    "institution.trainingSessions.title": "Sessioni di formazione",
    "institution.trainingSessions.title_education": "Lezioni",
    "institution.trainingSessions.subtitle": "Pianifica le sessioni, iscrivi i partecipanti, segui presenze e tracciabilità (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Pianifica le lezioni delle tue classi e fai l’appello.",
    "institution.trainingSessions.ownScopeHint": "Vedi le sessioni che conduci o che hai creato.",
    "institution.trainingSessions.ownScopeHint_education": "Vedi le lezioni di cui sei docente o autore.",
    "institution.trainingSessions.refresh": "Aggiorna",
    "institution.trainingSessions.new": "Nuova sessione",
    "institution.trainingSessions.new_education": "Nuova lezione",
    "institution.trainingSessions.loadError": "Impossibile caricare le sessioni.",
    "institution.trainingSessions.loadError_education": "Impossibile caricare le lezioni.",
    "institution.trainingSessions.search": "Cerca (titolo, codice)…",
    "institution.trainingSessions.statusAll": "Tutti gli stati",
    "institution.trainingSessions.status.draft": "Bozza",
    "institution.trainingSessions.status.planned": "Pianificata",
    "institution.trainingSessions.status.in_progress": "In corso",
    "institution.trainingSessions.status.completed": "Terminata",
    "institution.trainingSessions.status.cancelled": "Annullata",
    "institution.trainingSessions.empty": "Nessuna sessione.",
    "institution.trainingSessions.empty_education": "Nessuna lezione.",
    "institution.trainingSessions.total": "Totale: {{count}}",
    "institution.trainingSessions.detailHint": "Seleziona una sessione dall’elenco o creane una.",
    "institution.trainingSessions.detailHint_education": "Seleziona una lezione dall’elenco o creane una.",
    "institution.trainingSessions.formFlow": "Formazione",
    "institution.trainingSessions.formFlow_education": "Corso",
    "institution.trainingSessions.formCode": "Codice sessione",
    "institution.trainingSessions.formCode_education": "Codice lezione",
    "institution.trainingSessions.suggestCode": "Suggerisci",
    "institution.trainingSessions.formTitle": "Titolo",
    "institution.trainingSessions.formModality": "Modalità",
    "institution.trainingSessions.modality.presentiel": "In presenza",
    "institution.trainingSessions.modality.presentiel_education": "In classe",
    "institution.trainingSessions.modality.distanciel": "A distanza",
    "institution.trainingSessions.modality.distanciel_education": "A distanza",
    "institution.trainingSessions.modality.mixte": "Mista",
    "institution.trainingSessions.modality.mixte_education": "Ibrida",
    "institution.trainingSessions.formStatus": "Stato",
    "institution.trainingSessions.formStart": "Inizio",
    "institution.trainingSessions.formEnd": "Fine",
    "institution.trainingSessions.formLocation": "Luogo",
    "institution.trainingSessions.formLocation_education": "Aula",
    "institution.trainingSessions.formVisio": "Link della videoconferenza",
    "institution.trainingSessions.formMaxSeats": "Posti massimi",
    "institution.trainingSessions.formTrainer": "Formatore / conduttore",
    "institution.trainingSessions.formTrainer_education": "Docente",
    "institution.trainingSessions.formTrainerNone": "— Nessuno —",
    "institution.trainingSessions.formGroup": "Gruppo di partecipanti",
    "institution.trainingSessions.formGroup_education": "Gruppo di studenti",
    "institution.trainingSessions.formGroupNone": "— Nessuno —",
    "institution.trainingSessions.sectionQualiopi": "Tracciabilità Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "Convocazione inviata il",
    "institution.trainingSessions.programmeSent": "Programma inviato il",
    "institution.trainingSessions.rulesSent": "Regolamento interno inviato il",
    "institution.trainingSessions.dropoutNotes": "Monitoraggio degli abbandoni (azioni svolte)",
    "institution.trainingSessions.adminNotes": "Note amministrative (finanziamento, BPF)",
    "institution.trainingSessions.save": "Salva",
    "institution.trainingSessions.saveError": "Impossibile salvare.",
    "institution.trainingSessions.requiredFields": "Indica formazione, titolo, inizio e fine.",
    "institution.trainingSessions.requiredFields_education": "Indica corso, titolo, inizio e fine.",
    "institution.trainingSessions.actionError": "Azione impossibile.",
    "institution.trainingSessions.delete": "Elimina",
    "institution.trainingSessions.deleteConfirm": "Eliminare questa sessione?",
    "institution.trainingSessions.deleteConfirm_education": "Eliminare questa lezione?",
    "institution.trainingSessions.attendees": "Iscritti",
    "institution.trainingSessions.attendees_education": "Studenti",
    "institution.trainingSessions.addAttendees": "Iscrivi partecipanti",
    "institution.trainingSessions.addAttendees_education": "Aggiungi studenti",
    "institution.trainingSessions.learnerSearch": "Cerca un partecipante (almeno 2 caratteri)",
    "institution.trainingSessions.learnerSearch_education": "Cerca uno studente (almeno 2 caratteri)",
    "institution.trainingSessions.enrollInFlow": "Iscrivi anche alla formazione",
    "institution.trainingSessions.enrollInFlow_education": "Dai anche accesso al corso",
    "institution.trainingSessions.addSelected": "Aggiungi la selezione",
    "institution.trainingSessions.removeAttendee": "Rimuovi",
    "institution.trainingSessions.attendeeConvocation": "Convocazione inviata il",
    "institution.trainingSessions.attendeeDropout": "Azioni contro l’abbandono",
    "institution.trainingSessions.attended": "Presenza confermata",
    "institution.trainingSessions.attended_education": "Presente",
    "institution.trainingSessions.withdrawn": "Abbandono il",
    "institution.trainingSessions.saveAttendee": "Salva",
    "institution.trainingSessions.edit": "Modifica",
    "institution.trainingSessions.cancel": "Annulla",
    "institution.trainingSessions.classes": "Classi",
    "institution.trainingSessions.classFilterAll": "Tutte le classi",
    "institution.trainingSessions.addClass": "Aggiungi un’intera classe",
    "institution.trainingSessions.chooseClass": "Scegli una classe",
    "institution.trainingSessions.addClassButton": "Aggiungi la classe",
    "institution.trainingSessions.rollCall": "Appello",
    "institution.trainingSessions.present": "Presente",
    "institution.trainingSessions.absent": "Assente",
    "institution.trainingSessions.presentCount": "Presenti: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "Tutti presenti",
    "institution.evaluations.classAverage": "Media della classe",
    "institution.evaluations.classAverage_education": "Media della classe",
    "institution.evaluations.gradedCount": "Valutati: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Studenti valutati: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Ancora nessun voto per calcolare una media.",
    "institution.evaluations.gradeOn20": "Voto /20",
    "institution.evaluations.rawPoints": "{{value}} pt",
    "profile.school.title": "Le mie classi",
    "profile.school.titleTeacher": "Classi in cui insegno",
    "profile.school.empty": "Nessuna classe per ora.",
    "profile.school.loadError": "Impossibile caricare le tue classi.",
    "profile.school.teachers": "Docenti",
    "profile.school.noTeacher": "Nessun docente indicato.",
    "profile.school.leadSuffix": "coordinatore di classe",
    "profile.school.role": "Profilo",
    "profile.school.classRole.student": "Studente",
    "profile.school.classRole.teacher": "Docente",
    "profile.school.classRole.lead": "Coordinatore di classe",
    "profile.school.profileRole.student": "Studente",
    "profile.school.profileRole.teacher": "Docente",
    "profile.school.profileRole.direction": "Direzione",
    "learner.header.workspace_education": "Area studente",
    "learner.tabs.formations_education": "I miei corsi",
    "learner.sidebar.tagline_education": "Studente",
    "learner.formations.title_education": "I miei corsi",
    "learner.formations.searchPlaceholder_education": "Cerca un corso…",
    "learner.formations.loading_education": "Caricamento dei tuoi corsi…",
    "learner.formations.empty_education": "Nessun corso trovato.",
    "learner.formations.instructor_education": "Docente",
    "learner.home.defaultName_education": "studente",
    "learner.home.resumeMyFormation_education": "Riprendi il mio corso",
    "learner.home.resumeTraining_education": "Riprendi i tuoi corsi",
    "learner.progress.completedFormations_education": "Corsi completati",
    "learner.sidebar.backToTrainer_education": "Dashboard docente",
    "learner.sidebar.backToTrainerAria_education": "Torna alla dashboard docente",
  },
  pt: {
    "institution.tabs.trainingSessions": "Sessões",
    "institution.tabs.trainingSessions_education": "Aulas",
    "institution.trainingSessions.title": "Sessões de formação",
    "institution.trainingSessions.title_education": "Aulas",
    "institution.trainingSessions.subtitle": "Planeie as sessões, inscreva os formandos e acompanhe as presenças e a rastreabilidade (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Planeie as aulas das suas turmas e faça a chamada.",
    "institution.trainingSessions.ownScopeHint": "Vê as sessões que dinamiza ou que criou.",
    "institution.trainingSessions.ownScopeHint_education": "Vê as aulas de que é professor ou autor.",
    "institution.trainingSessions.refresh": "Atualizar",
    "institution.trainingSessions.new": "Nova sessão",
    "institution.trainingSessions.new_education": "Nova aula",
    "institution.trainingSessions.loadError": "Não é possível carregar as sessões.",
    "institution.trainingSessions.loadError_education": "Não é possível carregar as aulas.",
    "institution.trainingSessions.search": "Pesquisar (título, código)…",
    "institution.trainingSessions.statusAll": "Todos os estados",
    "institution.trainingSessions.status.draft": "Rascunho",
    "institution.trainingSessions.status.planned": "Planeada",
    "institution.trainingSessions.status.in_progress": "Em curso",
    "institution.trainingSessions.status.completed": "Concluída",
    "institution.trainingSessions.status.cancelled": "Cancelada",
    "institution.trainingSessions.empty": "Nenhuma sessão.",
    "institution.trainingSessions.empty_education": "Nenhuma aula.",
    "institution.trainingSessions.total": "Total: {{count}}",
    "institution.trainingSessions.detailHint": "Selecione uma sessão na lista ou crie uma.",
    "institution.trainingSessions.detailHint_education": "Selecione uma aula na lista ou crie uma.",
    "institution.trainingSessions.formFlow": "Formação",
    "institution.trainingSessions.formFlow_education": "Curso",
    "institution.trainingSessions.formCode": "Código da sessão",
    "institution.trainingSessions.formCode_education": "Código da aula",
    "institution.trainingSessions.suggestCode": "Sugerir",
    "institution.trainingSessions.formTitle": "Título",
    "institution.trainingSessions.formModality": "Modalidade",
    "institution.trainingSessions.modality.presentiel": "Presencial",
    "institution.trainingSessions.modality.presentiel_education": "Em sala",
    "institution.trainingSessions.modality.distanciel": "À distância",
    "institution.trainingSessions.modality.distanciel_education": "À distância",
    "institution.trainingSessions.modality.mixte": "Mista",
    "institution.trainingSessions.modality.mixte_education": "Híbrida",
    "institution.trainingSessions.formStatus": "Estado",
    "institution.trainingSessions.formStart": "Início",
    "institution.trainingSessions.formEnd": "Fim",
    "institution.trainingSessions.formLocation": "Local",
    "institution.trainingSessions.formLocation_education": "Sala",
    "institution.trainingSessions.formVisio": "Ligação de videoconferência",
    "institution.trainingSessions.formMaxSeats": "Lugares máximos",
    "institution.trainingSessions.formTrainer": "Formador / dinamizador",
    "institution.trainingSessions.formTrainer_education": "Professor",
    "institution.trainingSessions.formTrainerNone": "— Nenhum —",
    "institution.trainingSessions.formGroup": "Grupo de formandos",
    "institution.trainingSessions.formGroup_education": "Grupo de alunos",
    "institution.trainingSessions.formGroupNone": "— Nenhum —",
    "institution.trainingSessions.sectionQualiopi": "Rastreabilidade Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "Convocatória enviada em",
    "institution.trainingSessions.programmeSent": "Programa enviado em",
    "institution.trainingSessions.rulesSent": "Regulamento interno enviado em",
    "institution.trainingSessions.dropoutNotes": "Acompanhamento de desistências (ações realizadas)",
    "institution.trainingSessions.adminNotes": "Notas administrativas (financiamento, BPF)",
    "institution.trainingSessions.save": "Guardar",
    "institution.trainingSessions.saveError": "Não é possível guardar.",
    "institution.trainingSessions.requiredFields": "Indique a formação, o título, o início e o fim.",
    "institution.trainingSessions.requiredFields_education": "Indique o curso, o título, o início e o fim.",
    "institution.trainingSessions.actionError": "Ação impossível.",
    "institution.trainingSessions.delete": "Eliminar",
    "institution.trainingSessions.deleteConfirm": "Eliminar esta sessão?",
    "institution.trainingSessions.deleteConfirm_education": "Eliminar esta aula?",
    "institution.trainingSessions.attendees": "Inscritos",
    "institution.trainingSessions.attendees_education": "Alunos",
    "institution.trainingSessions.addAttendees": "Inscrever formandos",
    "institution.trainingSessions.addAttendees_education": "Adicionar alunos",
    "institution.trainingSessions.learnerSearch": "Pesquisar um formando (mínimo 2 caracteres)",
    "institution.trainingSessions.learnerSearch_education": "Pesquisar um aluno (mínimo 2 caracteres)",
    "institution.trainingSessions.enrollInFlow": "Inscrever também na formação",
    "institution.trainingSessions.enrollInFlow_education": "Dar também acesso ao curso",
    "institution.trainingSessions.addSelected": "Adicionar a seleção",
    "institution.trainingSessions.removeAttendee": "Remover",
    "institution.trainingSessions.attendeeConvocation": "Convocatória enviada em",
    "institution.trainingSessions.attendeeDropout": "Ações contra a desistência",
    "institution.trainingSessions.attended": "Presença confirmada",
    "institution.trainingSessions.attended_education": "Presente",
    "institution.trainingSessions.withdrawn": "Desistência em",
    "institution.trainingSessions.saveAttendee": "Guardar",
    "institution.trainingSessions.edit": "Editar",
    "institution.trainingSessions.cancel": "Cancelar",
    "institution.trainingSessions.classes": "Turmas",
    "institution.trainingSessions.classFilterAll": "Todas as turmas",
    "institution.trainingSessions.addClass": "Adicionar uma turma inteira",
    "institution.trainingSessions.chooseClass": "Escolher uma turma",
    "institution.trainingSessions.addClassButton": "Adicionar a turma",
    "institution.trainingSessions.rollCall": "Chamada",
    "institution.trainingSessions.present": "Presente",
    "institution.trainingSessions.absent": "Ausente",
    "institution.trainingSessions.presentCount": "Presentes: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "Todos presentes",
    "institution.evaluations.classAverage": "Média da turma",
    "institution.evaluations.classAverage_education": "Média da turma",
    "institution.evaluations.gradedCount": "Avaliados: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Alunos avaliados: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Ainda não há notas para calcular uma média.",
    "institution.evaluations.gradeOn20": "Nota /20",
    "institution.evaluations.rawPoints": "{{value}} pts",
    "profile.school.title": "As minhas turmas",
    "profile.school.titleTeacher": "Turmas que leciono",
    "profile.school.empty": "Nenhuma turma por enquanto.",
    "profile.school.loadError": "Não é possível carregar as suas turmas.",
    "profile.school.teachers": "Professores",
    "profile.school.noTeacher": "Nenhum professor indicado.",
    "profile.school.leadSuffix": "diretor de turma",
    "profile.school.role": "Perfil",
    "profile.school.classRole.student": "Aluno",
    "profile.school.classRole.teacher": "Professor",
    "profile.school.classRole.lead": "Diretor de turma",
    "profile.school.profileRole.student": "Aluno",
    "profile.school.profileRole.teacher": "Professor",
    "profile.school.profileRole.direction": "Direção",
    "learner.header.workspace_education": "Espaço do aluno",
    "learner.tabs.formations_education": "Os meus cursos",
    "learner.sidebar.tagline_education": "Aluno",
    "learner.formations.title_education": "Os meus cursos",
    "learner.formations.searchPlaceholder_education": "Pesquisar um curso…",
    "learner.formations.loading_education": "A carregar os seus cursos…",
    "learner.formations.empty_education": "Nenhum curso encontrado.",
    "learner.formations.instructor_education": "Professor",
    "learner.home.defaultName_education": "aluno",
    "learner.home.resumeMyFormation_education": "Retomar o meu curso",
    "learner.home.resumeTraining_education": "Retomar os seus cursos",
    "learner.progress.completedFormations_education": "Cursos concluídos",
    "learner.sidebar.backToTrainer_education": "Painel do professor",
    "learner.sidebar.backToTrainerAria_education": "Voltar ao painel do professor",
  },
  lb: {
    "institution.tabs.trainingSessions": "Sessiounen",
    "institution.tabs.trainingSessions_education": "Schoulstonnen",
    "institution.trainingSessions.title": "Formatiounssessiounen",
    "institution.trainingSessions.title_education": "Schoulstonnen",
    "institution.trainingSessions.subtitle": "Sessioune plangen, Léierender aschreiwen, Presenz an Noweiser verfollegen (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Plangt d’Schoulstonne vun Äre Klassen a maacht den Appell.",
    "institution.trainingSessions.ownScopeHint": "Dir gesitt d’Sessiounen, déi Dir leet oder ugeluecht hutt.",
    "institution.trainingSessions.ownScopeHint_education": "Dir gesitt d’Stonnen, déi Dir hält oder ugeluecht hutt.",
    "institution.trainingSessions.refresh": "Aktualiséieren",
    "institution.trainingSessions.new": "Nei Sessioun",
    "institution.trainingSessions.new_education": "Nei Stonn",
    "institution.trainingSessions.loadError": "D’Sessioune kënnen net geluede ginn.",
    "institution.trainingSessions.loadError_education": "D’Stonne kënnen net geluede ginn.",
    "institution.trainingSessions.search": "Sichen (Titel, Code)…",
    "institution.trainingSessions.statusAll": "All Statussen",
    "institution.trainingSessions.status.draft": "Entworf",
    "institution.trainingSessions.status.planned": "Geplangt",
    "institution.trainingSessions.status.in_progress": "Am Gaang",
    "institution.trainingSessions.status.completed": "Ofgeschloss",
    "institution.trainingSessions.status.cancelled": "Annuléiert",
    "institution.trainingSessions.empty": "Keng Sessioun.",
    "institution.trainingSessions.empty_education": "Keng Stonn.",
    "institution.trainingSessions.total": "Total: {{count}}",
    "institution.trainingSessions.detailHint": "Wielt eng Sessioun an der Lëscht oder leet eng un.",
    "institution.trainingSessions.detailHint_education": "Wielt eng Stonn an der Lëscht oder leet eng un.",
    "institution.trainingSessions.formFlow": "Formatioun",
    "institution.trainingSessions.formFlow_education": "Cours",
    "institution.trainingSessions.formCode": "Code vun der Sessioun",
    "institution.trainingSessions.formCode_education": "Code vun der Stonn",
    "institution.trainingSessions.suggestCode": "Proposéieren",
    "institution.trainingSessions.formTitle": "Titel",
    "institution.trainingSessions.formModality": "Modalitéit",
    "institution.trainingSessions.modality.presentiel": "Presenz",
    "institution.trainingSessions.modality.presentiel_education": "An der Klass",
    "institution.trainingSessions.modality.distanciel": "Op Distanz",
    "institution.trainingSessions.modality.distanciel_education": "Op Distanz",
    "institution.trainingSessions.modality.mixte": "Gemëscht",
    "institution.trainingSessions.modality.mixte_education": "Hybrid",
    "institution.trainingSessions.formStatus": "Status",
    "institution.trainingSessions.formStart": "Ufank",
    "institution.trainingSessions.formEnd": "Enn",
    "institution.trainingSessions.formLocation": "Plaz",
    "institution.trainingSessions.formLocation_education": "Sall",
    "institution.trainingSessions.formVisio": "Link fir d’Videokonferenz",
    "institution.trainingSessions.formMaxSeats": "Maximal Plazen",
    "institution.trainingSessions.formTrainer": "Formateur / Moderator",
    "institution.trainingSessions.formTrainer_education": "Enseignant",
    "institution.trainingSessions.formTrainerNone": "— Keen —",
    "institution.trainingSessions.formGroup": "Grupp vu Léierenden",
    "institution.trainingSessions.formGroup_education": "Grupp vu Schüler",
    "institution.trainingSessions.formGroupNone": "— Keng —",
    "institution.trainingSessions.sectionQualiopi": "Noweiser Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "Convocatioun geschéckt de",
    "institution.trainingSessions.programmeSent": "Programm geschéckt de",
    "institution.trainingSessions.rulesSent": "Hausuerdnung geschéckt de",
    "institution.trainingSessions.dropoutNotes": "Suivi vun den Ofbréch (Moossnamen)",
    "institution.trainingSessions.adminNotes": "Administrativ Notizen (Finanzéierung, BPF)",
    "institution.trainingSessions.save": "Späicheren",
    "institution.trainingSessions.saveError": "Späicheren net méiglech.",
    "institution.trainingSessions.requiredFields": "Gitt d’Formatioun, den Titel, den Ufank an d’Enn un.",
    "institution.trainingSessions.requiredFields_education": "Gitt de Cours, den Titel, den Ufank an d’Enn un.",
    "institution.trainingSessions.actionError": "Aktioun net méiglech.",
    "institution.trainingSessions.delete": "Läschen",
    "institution.trainingSessions.deleteConfirm": "Dës Sessioun läschen?",
    "institution.trainingSessions.deleteConfirm_education": "Dës Stonn läschen?",
    "institution.trainingSessions.attendees": "Ageschriwwen",
    "institution.trainingSessions.attendees_education": "Schüler",
    "institution.trainingSessions.addAttendees": "Léierender aschreiwen",
    "institution.trainingSessions.addAttendees_education": "Schüler derbäisetzen",
    "institution.trainingSessions.learnerSearch": "E Léierende sichen (mindestens 2 Zeechen)",
    "institution.trainingSessions.learnerSearch_education": "E Schüler sichen (mindestens 2 Zeechen)",
    "institution.trainingSessions.enrollInFlow": "Och an der Formatioun aschreiwen",
    "institution.trainingSessions.enrollInFlow_education": "Och Accès zum Cours ginn",
    "institution.trainingSessions.addSelected": "Auswiel derbäisetzen",
    "institution.trainingSessions.removeAttendee": "Ewechhuelen",
    "institution.trainingSessions.attendeeConvocation": "Convocatioun geschéckt de",
    "institution.trainingSessions.attendeeDropout": "Moossnamen géint den Ofbroch",
    "institution.trainingSessions.attended": "Presenz confirméiert",
    "institution.trainingSessions.attended_education": "Present",
    "institution.trainingSessions.withdrawn": "Ofgebrach de",
    "institution.trainingSessions.saveAttendee": "Späicheren",
    "institution.trainingSessions.edit": "Änneren",
    "institution.trainingSessions.cancel": "Ofbriechen",
    "institution.trainingSessions.classes": "Klassen",
    "institution.trainingSessions.classFilterAll": "All Klassen",
    "institution.trainingSessions.addClass": "Eng ganz Klass derbäisetzen",
    "institution.trainingSessions.chooseClass": "Eng Klass wielen",
    "institution.trainingSessions.addClassButton": "D’Klass derbäisetzen",
    "institution.trainingSessions.rollCall": "Appell",
    "institution.trainingSessions.present": "Present",
    "institution.trainingSessions.absent": "Absent",
    "institution.trainingSessions.presentCount": "Present: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "All present",
    "institution.evaluations.classAverage": "Moyenne vun der Klass",
    "institution.evaluations.classAverage_education": "Klassemoyenne",
    "institution.evaluations.gradedCount": "Bewäert: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Bewäert Schüler: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Nach keng Nott fir eng Moyenne ze berechnen.",
    "institution.evaluations.gradeOn20": "Nott /20",
    "institution.evaluations.rawPoints": "{{value}} Pkt.",
    "profile.school.title": "Meng Klassen",
    "profile.school.titleTeacher": "Klassen, déi ech ënnerriichten",
    "profile.school.empty": "Nach keng Klass.",
    "profile.school.loadError": "Är Klasse kënnen net geluede ginn.",
    "profile.school.teachers": "Enseignanten",
    "profile.school.noTeacher": "Keen Enseignant uginn.",
    "profile.school.leadSuffix": "Klasseregent",
    "profile.school.role": "Profil",
    "profile.school.classRole.student": "Schüler",
    "profile.school.classRole.teacher": "Enseignant",
    "profile.school.classRole.lead": "Klasseregent",
    "profile.school.profileRole.student": "Schüler",
    "profile.school.profileRole.teacher": "Enseignant",
    "profile.school.profileRole.direction": "Direktioun",
    "learner.header.workspace_education": "Schülerberäich",
    "learner.tabs.formations_education": "Meng Couren",
    "learner.sidebar.tagline_education": "Schüler",
    "learner.formations.title_education": "Meng Couren",
    "learner.formations.searchPlaceholder_education": "E Cours sichen…",
    "learner.formations.loading_education": "Är Coure gi gelueden…",
    "learner.formations.empty_education": "Kee Cours fonnt.",
    "learner.formations.instructor_education": "Enseignant",
    "learner.home.defaultName_education": "Schüler",
    "learner.home.resumeMyFormation_education": "Mäi Cours weiderféieren",
    "learner.home.resumeTraining_education": "Är Coure weiderféieren",
    "learner.progress.completedFormations_education": "Ofgeschloss Couren",
    "learner.sidebar.backToTrainer_education": "Tableau de bord Enseignant",
    "learner.sidebar.backToTrainerAria_education": "Zréck op den Tableau de bord Enseignant",
  },
  uk: {
    "institution.tabs.trainingSessions": "Сесії",
    "institution.tabs.trainingSessions_education": "Уроки",
    "institution.trainingSessions.title": "Навчальні сесії",
    "institution.trainingSessions.title_education": "Уроки",
    "institution.trainingSessions.subtitle": "Плануйте сесії, записуйте учасників, відстежуйте відвідування та документування (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Плануйте уроки своїх класів і проводьте перекличку.",
    "institution.trainingSessions.ownScopeHint": "Ви бачите сесії, які ведете або створили.",
    "institution.trainingSessions.ownScopeHint_education": "Ви бачите уроки, які викладаєте або створили.",
    "institution.trainingSessions.refresh": "Оновити",
    "institution.trainingSessions.new": "Нова сесія",
    "institution.trainingSessions.new_education": "Новий урок",
    "institution.trainingSessions.loadError": "Не вдалося завантажити сесії.",
    "institution.trainingSessions.loadError_education": "Не вдалося завантажити уроки.",
    "institution.trainingSessions.search": "Пошук (назва, код)…",
    "institution.trainingSessions.statusAll": "Усі статуси",
    "institution.trainingSessions.status.draft": "Чернетка",
    "institution.trainingSessions.status.planned": "Заплановано",
    "institution.trainingSessions.status.in_progress": "Триває",
    "institution.trainingSessions.status.completed": "Завершено",
    "institution.trainingSessions.status.cancelled": "Скасовано",
    "institution.trainingSessions.empty": "Немає сесій.",
    "institution.trainingSessions.empty_education": "Немає уроків.",
    "institution.trainingSessions.total": "Усього: {{count}}",
    "institution.trainingSessions.detailHint": "Виберіть сесію у списку або створіть нову.",
    "institution.trainingSessions.detailHint_education": "Виберіть урок у списку або створіть новий.",
    "institution.trainingSessions.formFlow": "Навчання",
    "institution.trainingSessions.formFlow_education": "Курс",
    "institution.trainingSessions.formCode": "Код сесії",
    "institution.trainingSessions.formCode_education": "Код уроку",
    "institution.trainingSessions.suggestCode": "Запропонувати",
    "institution.trainingSessions.formTitle": "Назва",
    "institution.trainingSessions.formModality": "Формат",
    "institution.trainingSessions.modality.presentiel": "Очно",
    "institution.trainingSessions.modality.presentiel_education": "У класі",
    "institution.trainingSessions.modality.distanciel": "Дистанційно",
    "institution.trainingSessions.modality.distanciel_education": "Дистанційно",
    "institution.trainingSessions.modality.mixte": "Змішано",
    "institution.trainingSessions.modality.mixte_education": "Гібридно",
    "institution.trainingSessions.formStatus": "Статус",
    "institution.trainingSessions.formStart": "Початок",
    "institution.trainingSessions.formEnd": "Кінець",
    "institution.trainingSessions.formLocation": "Місце",
    "institution.trainingSessions.formLocation_education": "Аудиторія",
    "institution.trainingSessions.formVisio": "Посилання на відеоконференцію",
    "institution.trainingSessions.formMaxSeats": "Максимум місць",
    "institution.trainingSessions.formTrainer": "Тренер / ведучий",
    "institution.trainingSessions.formTrainer_education": "Учитель",
    "institution.trainingSessions.formTrainerNone": "— Немає —",
    "institution.trainingSessions.formGroup": "Група учасників",
    "institution.trainingSessions.formGroup_education": "Група учнів",
    "institution.trainingSessions.formGroupNone": "— Немає —",
    "institution.trainingSessions.sectionQualiopi": "Документування Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "Запрошення надіслано",
    "institution.trainingSessions.programmeSent": "Програму надіслано",
    "institution.trainingSessions.rulesSent": "Внутрішній регламент надіслано",
    "institution.trainingSessions.dropoutNotes": "Супровід вибуття (вжиті заходи)",
    "institution.trainingSessions.adminNotes": "Адміністративні нотатки (фінансування, BPF)",
    "institution.trainingSessions.save": "Зберегти",
    "institution.trainingSessions.saveError": "Не вдалося зберегти.",
    "institution.trainingSessions.requiredFields": "Укажіть навчання, назву, початок і кінець.",
    "institution.trainingSessions.requiredFields_education": "Укажіть курс, назву, початок і кінець.",
    "institution.trainingSessions.actionError": "Дія неможлива.",
    "institution.trainingSessions.delete": "Видалити",
    "institution.trainingSessions.deleteConfirm": "Видалити цю сесію?",
    "institution.trainingSessions.deleteConfirm_education": "Видалити цей урок?",
    "institution.trainingSessions.attendees": "Записані",
    "institution.trainingSessions.attendees_education": "Учні",
    "institution.trainingSessions.addAttendees": "Записати учасників",
    "institution.trainingSessions.addAttendees_education": "Додати учнів",
    "institution.trainingSessions.learnerSearch": "Пошук учасника (щонайменше 2 символи)",
    "institution.trainingSessions.learnerSearch_education": "Пошук учня (щонайменше 2 символи)",
    "institution.trainingSessions.enrollInFlow": "Також записати на навчання",
    "institution.trainingSessions.enrollInFlow_education": "Також надати доступ до курсу",
    "institution.trainingSessions.addSelected": "Додати вибраних",
    "institution.trainingSessions.removeAttendee": "Вилучити",
    "institution.trainingSessions.attendeeConvocation": "Запрошення надіслано",
    "institution.trainingSessions.attendeeDropout": "Заходи проти вибуття",
    "institution.trainingSessions.attended": "Присутність підтверджено",
    "institution.trainingSessions.attended_education": "Присутній",
    "institution.trainingSessions.withdrawn": "Вибув",
    "institution.trainingSessions.saveAttendee": "Зберегти",
    "institution.trainingSessions.edit": "Редагувати",
    "institution.trainingSessions.cancel": "Скасувати",
    "institution.trainingSessions.classes": "Класи",
    "institution.trainingSessions.classFilterAll": "Усі класи",
    "institution.trainingSessions.addClass": "Додати весь клас",
    "institution.trainingSessions.chooseClass": "Виберіть клас",
    "institution.trainingSessions.addClassButton": "Додати клас",
    "institution.trainingSessions.rollCall": "Перекличка",
    "institution.trainingSessions.present": "Присутній",
    "institution.trainingSessions.absent": "Відсутній",
    "institution.trainingSessions.presentCount": "Присутні: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "Усі присутні",
    "institution.evaluations.classAverage": "Середній бал класу",
    "institution.evaluations.classAverage_education": "Середній бал класу",
    "institution.evaluations.gradedCount": "Оцінено: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Оцінено учнів: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Ще немає оцінок для обчислення середнього.",
    "institution.evaluations.gradeOn20": "Оцінка /20",
    "institution.evaluations.rawPoints": "{{value}} б.",
    "profile.school.title": "Мої класи",
    "profile.school.titleTeacher": "Класи, у яких я викладаю",
    "profile.school.empty": "Поки що немає класів.",
    "profile.school.loadError": "Не вдалося завантажити ваші класи.",
    "profile.school.teachers": "Учителі",
    "profile.school.noTeacher": "Учителя не вказано.",
    "profile.school.leadSuffix": "класний керівник",
    "profile.school.role": "Профіль",
    "profile.school.classRole.student": "Учень",
    "profile.school.classRole.teacher": "Учитель",
    "profile.school.classRole.lead": "Класний керівник",
    "profile.school.profileRole.student": "Учень",
    "profile.school.profileRole.teacher": "Учитель",
    "profile.school.profileRole.direction": "Адміністрація",
    "learner.header.workspace_education": "Простір учня",
    "learner.tabs.formations_education": "Мої курси",
    "learner.sidebar.tagline_education": "Учень",
    "learner.formations.title_education": "Мої курси",
    "learner.formations.searchPlaceholder_education": "Пошук курсу…",
    "learner.formations.loading_education": "Завантаження ваших курсів…",
    "learner.formations.empty_education": "Курсів не знайдено.",
    "learner.formations.instructor_education": "Учитель",
    "learner.home.defaultName_education": "учню",
    "learner.home.resumeMyFormation_education": "Продовжити мій курс",
    "learner.home.resumeTraining_education": "Продовжити курси",
    "learner.progress.completedFormations_education": "Завершені курси",
    "learner.sidebar.backToTrainer_education": "Панель учителя",
    "learner.sidebar.backToTrainerAria_education": "Повернутися до панелі учителя",
  },
  ru: {
    "institution.tabs.trainingSessions": "Сессии",
    "institution.tabs.trainingSessions_education": "Уроки",
    "institution.trainingSessions.title": "Учебные сессии",
    "institution.trainingSessions.title_education": "Уроки",
    "institution.trainingSessions.subtitle": "Планируйте сессии, записывайте участников, отслеживайте посещаемость и документирование (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Планируйте уроки своих классов и проводите перекличку.",
    "institution.trainingSessions.ownScopeHint": "Вы видите сессии, которые ведёте или создали.",
    "institution.trainingSessions.ownScopeHint_education": "Вы видите уроки, которые преподаёте или создали.",
    "institution.trainingSessions.refresh": "Обновить",
    "institution.trainingSessions.new": "Новая сессия",
    "institution.trainingSessions.new_education": "Новый урок",
    "institution.trainingSessions.loadError": "Не удалось загрузить сессии.",
    "institution.trainingSessions.loadError_education": "Не удалось загрузить уроки.",
    "institution.trainingSessions.search": "Поиск (название, код)…",
    "institution.trainingSessions.statusAll": "Все статусы",
    "institution.trainingSessions.status.draft": "Черновик",
    "institution.trainingSessions.status.planned": "Запланировано",
    "institution.trainingSessions.status.in_progress": "Идёт",
    "institution.trainingSessions.status.completed": "Завершено",
    "institution.trainingSessions.status.cancelled": "Отменено",
    "institution.trainingSessions.empty": "Нет сессий.",
    "institution.trainingSessions.empty_education": "Нет уроков.",
    "institution.trainingSessions.total": "Всего: {{count}}",
    "institution.trainingSessions.detailHint": "Выберите сессию в списке или создайте новую.",
    "institution.trainingSessions.detailHint_education": "Выберите урок в списке или создайте новый.",
    "institution.trainingSessions.formFlow": "Обучение",
    "institution.trainingSessions.formFlow_education": "Курс",
    "institution.trainingSessions.formCode": "Код сессии",
    "institution.trainingSessions.formCode_education": "Код урока",
    "institution.trainingSessions.suggestCode": "Предложить",
    "institution.trainingSessions.formTitle": "Название",
    "institution.trainingSessions.formModality": "Формат",
    "institution.trainingSessions.modality.presentiel": "Очно",
    "institution.trainingSessions.modality.presentiel_education": "В классе",
    "institution.trainingSessions.modality.distanciel": "Дистанционно",
    "institution.trainingSessions.modality.distanciel_education": "Дистанционно",
    "institution.trainingSessions.modality.mixte": "Смешанно",
    "institution.trainingSessions.modality.mixte_education": "Гибридно",
    "institution.trainingSessions.formStatus": "Статус",
    "institution.trainingSessions.formStart": "Начало",
    "institution.trainingSessions.formEnd": "Окончание",
    "institution.trainingSessions.formLocation": "Место",
    "institution.trainingSessions.formLocation_education": "Кабинет",
    "institution.trainingSessions.formVisio": "Ссылка на видеоконференцию",
    "institution.trainingSessions.formMaxSeats": "Максимум мест",
    "institution.trainingSessions.formTrainer": "Тренер / ведущий",
    "institution.trainingSessions.formTrainer_education": "Учитель",
    "institution.trainingSessions.formTrainerNone": "— Нет —",
    "institution.trainingSessions.formGroup": "Группа участников",
    "institution.trainingSessions.formGroup_education": "Группа учеников",
    "institution.trainingSessions.formGroupNone": "— Нет —",
    "institution.trainingSessions.sectionQualiopi": "Документирование Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "Приглашение отправлено",
    "institution.trainingSessions.programmeSent": "Программа отправлена",
    "institution.trainingSessions.rulesSent": "Внутренний регламент отправлен",
    "institution.trainingSessions.dropoutNotes": "Сопровождение выбывания (принятые меры)",
    "institution.trainingSessions.adminNotes": "Административные заметки (финансирование, BPF)",
    "institution.trainingSessions.save": "Сохранить",
    "institution.trainingSessions.saveError": "Не удалось сохранить.",
    "institution.trainingSessions.requiredFields": "Укажите обучение, название, начало и окончание.",
    "institution.trainingSessions.requiredFields_education": "Укажите курс, название, начало и окончание.",
    "institution.trainingSessions.actionError": "Действие невозможно.",
    "institution.trainingSessions.delete": "Удалить",
    "institution.trainingSessions.deleteConfirm": "Удалить эту сессию?",
    "institution.trainingSessions.deleteConfirm_education": "Удалить этот урок?",
    "institution.trainingSessions.attendees": "Записанные",
    "institution.trainingSessions.attendees_education": "Ученики",
    "institution.trainingSessions.addAttendees": "Записать участников",
    "institution.trainingSessions.addAttendees_education": "Добавить учеников",
    "institution.trainingSessions.learnerSearch": "Поиск участника (не менее 2 символов)",
    "institution.trainingSessions.learnerSearch_education": "Поиск ученика (не менее 2 символов)",
    "institution.trainingSessions.enrollInFlow": "Также записать на обучение",
    "institution.trainingSessions.enrollInFlow_education": "Также дать доступ к курсу",
    "institution.trainingSessions.addSelected": "Добавить выбранных",
    "institution.trainingSessions.removeAttendee": "Убрать",
    "institution.trainingSessions.attendeeConvocation": "Приглашение отправлено",
    "institution.trainingSessions.attendeeDropout": "Меры против выбывания",
    "institution.trainingSessions.attended": "Присутствие подтверждено",
    "institution.trainingSessions.attended_education": "Присутствует",
    "institution.trainingSessions.withdrawn": "Выбыл",
    "institution.trainingSessions.saveAttendee": "Сохранить",
    "institution.trainingSessions.edit": "Изменить",
    "institution.trainingSessions.cancel": "Отмена",
    "institution.trainingSessions.classes": "Классы",
    "institution.trainingSessions.classFilterAll": "Все классы",
    "institution.trainingSessions.addClass": "Добавить весь класс",
    "institution.trainingSessions.chooseClass": "Выберите класс",
    "institution.trainingSessions.addClassButton": "Добавить класс",
    "institution.trainingSessions.rollCall": "Перекличка",
    "institution.trainingSessions.present": "Присутствует",
    "institution.trainingSessions.absent": "Отсутствует",
    "institution.trainingSessions.presentCount": "Присутствуют: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "Все присутствуют",
    "institution.evaluations.classAverage": "Средний балл класса",
    "institution.evaluations.classAverage_education": "Средний балл класса",
    "institution.evaluations.gradedCount": "Оценено: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Оценено учеников: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Пока нет оценок для расчёта среднего.",
    "institution.evaluations.gradeOn20": "Оценка /20",
    "institution.evaluations.rawPoints": "{{value}} б.",
    "profile.school.title": "Мои классы",
    "profile.school.titleTeacher": "Классы, в которых я преподаю",
    "profile.school.empty": "Пока нет классов.",
    "profile.school.loadError": "Не удалось загрузить ваши классы.",
    "profile.school.teachers": "Учителя",
    "profile.school.noTeacher": "Учитель не указан.",
    "profile.school.leadSuffix": "классный руководитель",
    "profile.school.role": "Профиль",
    "profile.school.classRole.student": "Ученик",
    "profile.school.classRole.teacher": "Учитель",
    "profile.school.classRole.lead": "Классный руководитель",
    "profile.school.profileRole.student": "Ученик",
    "profile.school.profileRole.teacher": "Учитель",
    "profile.school.profileRole.direction": "Администрация",
    "learner.header.workspace_education": "Пространство ученика",
    "learner.tabs.formations_education": "Мои курсы",
    "learner.sidebar.tagline_education": "Ученик",
    "learner.formations.title_education": "Мои курсы",
    "learner.formations.searchPlaceholder_education": "Поиск курса…",
    "learner.formations.loading_education": "Загрузка ваших курсов…",
    "learner.formations.empty_education": "Курсы не найдены.",
    "learner.formations.instructor_education": "Учитель",
    "learner.home.defaultName_education": "ученик",
    "learner.home.resumeMyFormation_education": "Продолжить мой курс",
    "learner.home.resumeTraining_education": "Продолжить курсы",
    "learner.progress.completedFormations_education": "Завершённые курсы",
    "learner.sidebar.backToTrainer_education": "Панель учителя",
    "learner.sidebar.backToTrainerAria_education": "Вернуться к панели учителя",
  },
  ar: {
    "institution.tabs.trainingSessions": "الجلسات",
    "institution.tabs.trainingSessions_education": "الحصص",
    "institution.trainingSessions.title": "جلسات التكوين",
    "institution.trainingSessions.title_education": "الحصص الدراسية",
    "institution.trainingSessions.subtitle": "خطّط الجلسات، وسجّل المتعلمين، وتابع الحضور والتوثيق (Qualiopi، BPF).",
    "institution.trainingSessions.subtitle_education": "خطّط الحصص الدراسية لأقسامك وسجّل الحضور.",
    "institution.trainingSessions.ownScopeHint": "تظهر لك الجلسات التي تؤطرها أو أنشأتها.",
    "institution.trainingSessions.ownScopeHint_education": "تظهر لك الحصص التي تدرّسها أو أنشأتها.",
    "institution.trainingSessions.refresh": "تحديث",
    "institution.trainingSessions.new": "جلسة جديدة",
    "institution.trainingSessions.new_education": "حصة جديدة",
    "institution.trainingSessions.loadError": "تعذّر تحميل الجلسات.",
    "institution.trainingSessions.loadError_education": "تعذّر تحميل الحصص.",
    "institution.trainingSessions.search": "بحث (العنوان، الرمز)…",
    "institution.trainingSessions.statusAll": "كل الحالات",
    "institution.trainingSessions.status.draft": "مسودة",
    "institution.trainingSessions.status.planned": "مخطط لها",
    "institution.trainingSessions.status.in_progress": "جارية",
    "institution.trainingSessions.status.completed": "منتهية",
    "institution.trainingSessions.status.cancelled": "ملغاة",
    "institution.trainingSessions.empty": "لا توجد جلسات.",
    "institution.trainingSessions.empty_education": "لا توجد حصص.",
    "institution.trainingSessions.total": "المجموع: {{count}}",
    "institution.trainingSessions.detailHint": "اختر جلسة من القائمة أو أنشئ جلسة جديدة.",
    "institution.trainingSessions.detailHint_education": "اختر حصة من القائمة أو أنشئ حصة جديدة.",
    "institution.trainingSessions.formFlow": "التكوين",
    "institution.trainingSessions.formFlow_education": "الدرس",
    "institution.trainingSessions.formCode": "رمز الجلسة",
    "institution.trainingSessions.formCode_education": "رمز الحصة",
    "institution.trainingSessions.suggestCode": "اقتراح",
    "institution.trainingSessions.formTitle": "العنوان",
    "institution.trainingSessions.formModality": "الصيغة",
    "institution.trainingSessions.modality.presentiel": "حضوري",
    "institution.trainingSessions.modality.presentiel_education": "في القسم",
    "institution.trainingSessions.modality.distanciel": "عن بُعد",
    "institution.trainingSessions.modality.distanciel_education": "عن بُعد",
    "institution.trainingSessions.modality.mixte": "مختلط",
    "institution.trainingSessions.modality.mixte_education": "هجين",
    "institution.trainingSessions.formStatus": "الحالة",
    "institution.trainingSessions.formStart": "البداية",
    "institution.trainingSessions.formEnd": "النهاية",
    "institution.trainingSessions.formLocation": "المكان",
    "institution.trainingSessions.formLocation_education": "القاعة",
    "institution.trainingSessions.formVisio": "رابط مؤتمر الفيديو",
    "institution.trainingSessions.formMaxSeats": "الحد الأقصى للمقاعد",
    "institution.trainingSessions.formTrainer": "المكوّن / المنشّط",
    "institution.trainingSessions.formTrainer_education": "الأستاذ",
    "institution.trainingSessions.formTrainerNone": "— لا أحد —",
    "institution.trainingSessions.formGroup": "مجموعة المتعلمين",
    "institution.trainingSessions.formGroup_education": "مجموعة التلاميذ",
    "institution.trainingSessions.formGroupNone": "— لا شيء —",
    "institution.trainingSessions.sectionQualiopi": "توثيق Qualiopi / BPF",
    "institution.trainingSessions.convocationSent": "أُرسل الاستدعاء في",
    "institution.trainingSessions.programmeSent": "أُرسل البرنامج في",
    "institution.trainingSessions.rulesSent": "أُرسل النظام الداخلي في",
    "institution.trainingSessions.dropoutNotes": "متابعة الانقطاع (الإجراءات المتخذة)",
    "institution.trainingSessions.adminNotes": "ملاحظات إدارية (التمويل، BPF)",
    "institution.trainingSessions.save": "حفظ",
    "institution.trainingSessions.saveError": "تعذّر الحفظ.",
    "institution.trainingSessions.requiredFields": "أدخل التكوين والعنوان والبداية والنهاية.",
    "institution.trainingSessions.requiredFields_education": "أدخل الدرس والعنوان والبداية والنهاية.",
    "institution.trainingSessions.actionError": "تعذّر تنفيذ الإجراء.",
    "institution.trainingSessions.delete": "حذف",
    "institution.trainingSessions.deleteConfirm": "حذف هذه الجلسة؟",
    "institution.trainingSessions.deleteConfirm_education": "حذف هذه الحصة؟",
    "institution.trainingSessions.attendees": "المسجّلون",
    "institution.trainingSessions.attendees_education": "التلاميذ",
    "institution.trainingSessions.addAttendees": "تسجيل متعلمين",
    "institution.trainingSessions.addAttendees_education": "إضافة تلاميذ",
    "institution.trainingSessions.learnerSearch": "ابحث عن متعلم (حرفان على الأقل)",
    "institution.trainingSessions.learnerSearch_education": "ابحث عن تلميذ (حرفان على الأقل)",
    "institution.trainingSessions.enrollInFlow": "التسجيل في التكوين أيضًا",
    "institution.trainingSessions.enrollInFlow_education": "منح الوصول إلى الدرس أيضًا",
    "institution.trainingSessions.addSelected": "إضافة المحدَّدين",
    "institution.trainingSessions.removeAttendee": "إزالة",
    "institution.trainingSessions.attendeeConvocation": "أُرسل الاستدعاء في",
    "institution.trainingSessions.attendeeDropout": "إجراءات الوقاية من الانقطاع",
    "institution.trainingSessions.attended": "تم تأكيد الحضور",
    "institution.trainingSessions.attended_education": "حاضر",
    "institution.trainingSessions.withdrawn": "انقطع في",
    "institution.trainingSessions.saveAttendee": "حفظ",
    "institution.trainingSessions.edit": "تعديل",
    "institution.trainingSessions.cancel": "إلغاء",
    "institution.trainingSessions.classes": "الأقسام",
    "institution.trainingSessions.classFilterAll": "كل الأقسام",
    "institution.trainingSessions.addClass": "إضافة قسم كامل",
    "institution.trainingSessions.chooseClass": "اختر قسمًا",
    "institution.trainingSessions.addClassButton": "إضافة القسم",
    "institution.trainingSessions.rollCall": "تسجيل الحضور",
    "institution.trainingSessions.present": "حاضر",
    "institution.trainingSessions.absent": "غائب",
    "institution.trainingSessions.presentCount": "الحاضرون: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "الجميع حاضرون",
    "institution.evaluations.classAverage": "معدل القسم",
    "institution.evaluations.classAverage_education": "معدل القسم",
    "institution.evaluations.gradedCount": "المقيَّمون: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "التلاميذ المقيَّمون: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "لا توجد علامات بعد لحساب المعدل.",
    "institution.evaluations.gradeOn20": "العلامة /20",
    "institution.evaluations.rawPoints": "{{value}} نقطة",
    "profile.school.title": "أقسامي",
    "profile.school.titleTeacher": "الأقسام التي أدرّسها",
    "profile.school.empty": "لا توجد أقسام حاليًا.",
    "profile.school.loadError": "تعذّر تحميل أقسامك.",
    "profile.school.teachers": "الأساتذة",
    "profile.school.noTeacher": "لم يُحدَّد أي أستاذ.",
    "profile.school.leadSuffix": "أستاذ رئيسي",
    "profile.school.role": "الملف",
    "profile.school.classRole.student": "تلميذ",
    "profile.school.classRole.teacher": "أستاذ",
    "profile.school.classRole.lead": "أستاذ رئيسي",
    "profile.school.profileRole.student": "تلميذ",
    "profile.school.profileRole.teacher": "أستاذ",
    "profile.school.profileRole.direction": "الإدارة",
    "learner.header.workspace_education": "فضاء التلميذ",
    "learner.tabs.formations_education": "دروسي",
    "learner.sidebar.tagline_education": "تلميذ",
    "learner.formations.title_education": "دروسي",
    "learner.formations.searchPlaceholder_education": "ابحث عن درس…",
    "learner.formations.loading_education": "جارٍ تحميل دروسك…",
    "learner.formations.empty_education": "لم يُعثر على أي درس.",
    "learner.formations.instructor_education": "الأستاذ",
    "learner.home.defaultName_education": "تلميذ",
    "learner.home.resumeMyFormation_education": "استئناف درسي",
    "learner.home.resumeTraining_education": "استأنف دروسك",
    "learner.progress.completedFormations_education": "الدروس المكتملة",
    "learner.sidebar.backToTrainer_education": "لوحة الأستاذ",
    "learner.sidebar.backToTrainerAria_education": "العودة إلى لوحة الأستاذ",
  },
  tr: {
    "institution.tabs.trainingSessions": "Oturumlar",
    "institution.tabs.trainingSessions_education": "Ders oturumları",
    "institution.trainingSessions.title": "Eğitim oturumları",
    "institution.trainingSessions.title_education": "Ders oturumları",
    "institution.trainingSessions.subtitle": "Oturumları planlayın, katılımcıları kaydedin, devamı ve izlenebilirliği takip edin (Qualiopi, BPF).",
    "institution.trainingSessions.subtitle_education": "Sınıflarınızın ders oturumlarını planlayın ve yoklama alın.",
    "institution.trainingSessions.ownScopeHint": "Yürüttüğünüz veya oluşturduğunuz oturumları görüyorsunuz.",
    "institution.trainingSessions.ownScopeHint_education": "Öğretmeni veya oluşturanı olduğunuz ders oturumlarını görüyorsunuz.",
    "institution.trainingSessions.refresh": "Yenile",
    "institution.trainingSessions.new": "Yeni oturum",
    "institution.trainingSessions.new_education": "Yeni ders oturumu",
    "institution.trainingSessions.loadError": "Oturumlar yüklenemiyor.",
    "institution.trainingSessions.loadError_education": "Ders oturumları yüklenemiyor.",
    "institution.trainingSessions.search": "Ara (başlık, kod)…",
    "institution.trainingSessions.statusAll": "Tüm durumlar",
    "institution.trainingSessions.status.draft": "Taslak",
    "institution.trainingSessions.status.planned": "Planlandı",
    "institution.trainingSessions.status.in_progress": "Devam ediyor",
    "institution.trainingSessions.status.completed": "Tamamlandı",
    "institution.trainingSessions.status.cancelled": "İptal edildi",
    "institution.trainingSessions.empty": "Oturum yok.",
    "institution.trainingSessions.empty_education": "Ders oturumu yok.",
    "institution.trainingSessions.total": "Toplam: {{count}}",
    "institution.trainingSessions.detailHint": "Listeden bir oturum seçin veya yeni bir oturum oluşturun.",
    "institution.trainingSessions.detailHint_education": "Listeden bir ders oturumu seçin veya yenisini oluşturun.",
    "institution.trainingSessions.formFlow": "Eğitim",
    "institution.trainingSessions.formFlow_education": "Ders",
    "institution.trainingSessions.formCode": "Oturum kodu",
    "institution.trainingSessions.formCode_education": "Ders oturumu kodu",
    "institution.trainingSessions.suggestCode": "Öner",
    "institution.trainingSessions.formTitle": "Başlık",
    "institution.trainingSessions.formModality": "Biçim",
    "institution.trainingSessions.modality.presentiel": "Yüz yüze",
    "institution.trainingSessions.modality.presentiel_education": "Sınıfta",
    "institution.trainingSessions.modality.distanciel": "Uzaktan",
    "institution.trainingSessions.modality.distanciel_education": "Uzaktan",
    "institution.trainingSessions.modality.mixte": "Karma",
    "institution.trainingSessions.modality.mixte_education": "Hibrit",
    "institution.trainingSessions.formStatus": "Durum",
    "institution.trainingSessions.formStart": "Başlangıç",
    "institution.trainingSessions.formEnd": "Bitiş",
    "institution.trainingSessions.formLocation": "Yer",
    "institution.trainingSessions.formLocation_education": "Derslik",
    "institution.trainingSessions.formVisio": "Video konferans bağlantısı",
    "institution.trainingSessions.formMaxSeats": "Azami kontenjan",
    "institution.trainingSessions.formTrainer": "Eğitmen / moderatör",
    "institution.trainingSessions.formTrainer_education": "Öğretmen",
    "institution.trainingSessions.formTrainerNone": "— Yok —",
    "institution.trainingSessions.formGroup": "Katılımcı grubu",
    "institution.trainingSessions.formGroup_education": "Öğrenci grubu",
    "institution.trainingSessions.formGroupNone": "— Yok —",
    "institution.trainingSessions.sectionQualiopi": "Qualiopi / BPF izlenebilirliği",
    "institution.trainingSessions.convocationSent": "Davet gönderilme tarihi",
    "institution.trainingSessions.programmeSent": "Program gönderilme tarihi",
    "institution.trainingSessions.rulesSent": "İç yönetmelik gönderilme tarihi",
    "institution.trainingSessions.dropoutNotes": "Bırakma takibi (yapılan işlemler)",
    "institution.trainingSessions.adminNotes": "İdari notlar (finansman, BPF)",
    "institution.trainingSessions.save": "Kaydet",
    "institution.trainingSessions.saveError": "Kaydedilemiyor.",
    "institution.trainingSessions.requiredFields": "Eğitimi, başlığı, başlangıcı ve bitişi girin.",
    "institution.trainingSessions.requiredFields_education": "Dersi, başlığı, başlangıcı ve bitişi girin.",
    "institution.trainingSessions.actionError": "İşlem yapılamadı.",
    "institution.trainingSessions.delete": "Sil",
    "institution.trainingSessions.deleteConfirm": "Bu oturum silinsin mi?",
    "institution.trainingSessions.deleteConfirm_education": "Bu ders oturumu silinsin mi?",
    "institution.trainingSessions.attendees": "Kayıtlılar",
    "institution.trainingSessions.attendees_education": "Öğrenciler",
    "institution.trainingSessions.addAttendees": "Katılımcı kaydet",
    "institution.trainingSessions.addAttendees_education": "Öğrenci ekle",
    "institution.trainingSessions.learnerSearch": "Katılımcı ara (en az 2 karakter)",
    "institution.trainingSessions.learnerSearch_education": "Öğrenci ara (en az 2 karakter)",
    "institution.trainingSessions.enrollInFlow": "Eğitime de kaydet",
    "institution.trainingSessions.enrollInFlow_education": "Derse de erişim ver",
    "institution.trainingSessions.addSelected": "Seçimi ekle",
    "institution.trainingSessions.removeAttendee": "Çıkar",
    "institution.trainingSessions.attendeeConvocation": "Davet gönderilme tarihi",
    "institution.trainingSessions.attendeeDropout": "Bırakmayı önleme işlemleri",
    "institution.trainingSessions.attended": "Katılım onaylandı",
    "institution.trainingSessions.attended_education": "Var",
    "institution.trainingSessions.withdrawn": "Bırakma tarihi",
    "institution.trainingSessions.saveAttendee": "Kaydet",
    "institution.trainingSessions.edit": "Düzenle",
    "institution.trainingSessions.cancel": "İptal",
    "institution.trainingSessions.classes": "Sınıflar",
    "institution.trainingSessions.classFilterAll": "Tüm sınıflar",
    "institution.trainingSessions.addClass": "Bütün bir sınıfı ekle",
    "institution.trainingSessions.chooseClass": "Bir sınıf seçin",
    "institution.trainingSessions.addClassButton": "Sınıfı ekle",
    "institution.trainingSessions.rollCall": "Yoklama",
    "institution.trainingSessions.present": "Var",
    "institution.trainingSessions.absent": "Yok",
    "institution.trainingSessions.presentCount": "Gelenler: {{present}} / {{total}}",
    "institution.trainingSessions.markAllPresent": "Herkes var",
    "institution.evaluations.classAverage": "Sınıf ortalaması",
    "institution.evaluations.classAverage_education": "Sınıf ortalaması",
    "institution.evaluations.gradedCount": "Notlanan: {{count}} / {{total}}",
    "institution.evaluations.gradedCount_education": "Notlanan öğrenciler: {{count}} / {{total}}",
    "institution.evaluations.noAverage": "Ortalama hesaplamak için henüz not yok.",
    "institution.evaluations.gradeOn20": "Not /20",
    "institution.evaluations.rawPoints": "{{value}} puan",
    "profile.school.title": "Sınıflarım",
    "profile.school.titleTeacher": "Ders verdiğim sınıflar",
    "profile.school.empty": "Şimdilik sınıf yok.",
    "profile.school.loadError": "Sınıflarınız yüklenemiyor.",
    "profile.school.teachers": "Öğretmenler",
    "profile.school.noTeacher": "Belirtilmiş öğretmen yok.",
    "profile.school.leadSuffix": "sınıf öğretmeni",
    "profile.school.role": "Profil",
    "profile.school.classRole.student": "Öğrenci",
    "profile.school.classRole.teacher": "Öğretmen",
    "profile.school.classRole.lead": "Sınıf öğretmeni",
    "profile.school.profileRole.student": "Öğrenci",
    "profile.school.profileRole.teacher": "Öğretmen",
    "profile.school.profileRole.direction": "Okul yönetimi",
    "learner.header.workspace_education": "Öğrenci alanı",
    "learner.tabs.formations_education": "Derslerim",
    "learner.sidebar.tagline_education": "Öğrenci",
    "learner.formations.title_education": "Derslerim",
    "learner.formations.searchPlaceholder_education": "Ders ara…",
    "learner.formations.loading_education": "Dersleriniz yükleniyor…",
    "learner.formations.empty_education": "Ders bulunamadı.",
    "learner.formations.instructor_education": "Öğretmen",
    "learner.home.defaultName_education": "öğrenci",
    "learner.home.resumeMyFormation_education": "Dersime devam et",
    "learner.home.resumeTraining_education": "Derslerinize devam edin",
    "learner.progress.completedFormations_education": "Tamamlanan dersler",
    "learner.sidebar.backToTrainer_education": "Öğretmen paneli",
    "learner.sidebar.backToTrainerAria_education": "Öğretmen paneline dön",
  },
};
